import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import cn from "classnames";
import styles from "./style.scss";
import { Reference as ReferenceType } from "models/AiResearch";
import { SideHelp } from "features/SideHelp";
import { Article } from "./Article";
import ExternalImage from "images/icon/ExrternalLinkWhite.svg";
import { FinancialChart } from "./FinancialChart";
import { MacroChart } from "./MacroChart";
import { MacroIrChart } from "./MacroIrChart";
import { FinancialTable } from "./FinancialTable";
import { trackAction } from "utils/helper/analytics";
import { CompanyListTable } from "./CompanyListTable";
import { useReduxState } from "hooks/useReduxState";

const ReferenceContent = ({ reference }: { reference: ReferenceType }) => {
  switch (reference.dataType) {
    case "article_chunk":
      return <Article reference={reference} />;
    case "financial_chart":
      return <FinancialChart reference={reference} />;
    case "macro_chart":
      return <MacroChart reference={reference} />;
    case "macro_ir_chart":
      return <MacroIrChart reference={reference} />;
    case "financial_table":
      return <FinancialTable reference={reference} />;
    case "company_list_table":
      return <CompanyListTable reference={reference} />;
    default:
      return null;
  }
};

interface Props {
  reference?: ReferenceType | null;
  isLoading: boolean;
  position?: "fixed" | "sticky";
  top?: number;
  width?: number;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  onClose: () => void;
  onChangeWidth: (width: number) => void;
}

export const SideReference: React.FC<Props> = ({
  reference,
  isLoading,
  position,
  top,
  width,
  defaultWidth,
  minWidth,
  maxWidth,
  onClose,
  onChangeWidth
}) => {
  const { trialFlg } = useReduxState(state => state.auth.saved.user);

  // 引用情報の出典元リンクを表示するかのフラグ
  const isShowSourceLink = useMemo(() => {
    if (!reference || !reference.sourceLink) return false;

    // トライアル権限かつ AST or GBR の場合は出典リンクを表示しない
    // https://docs.google.com/spreadsheets/d/1MNs7QLYV_0zdlFIClr6xG0BpzeQsm2sZfIU_G215CcA/edit?gid=1088423806#gid=1088423806&range=H56
    if (
      trialFlg &&
      reference.dataType === "article_chunk" &&
      (reference.data.mediaCode === "AST" || reference.data.mediaCode === "GBR") // アスタミューゼ有望成長領域レポート or 日経業界分析レポート
    ) {
      return false;
    }

    return (
      reference.dataType === "article_chunk" ||
      reference.dataType === "macro_chart" ||
      reference.dataType === "financial_chart" ||
      reference.dataType === "financial_table"
    );
  }, [reference, trialFlg]);

  return (
    <SideHelp
      title="引用情報"
      isOpen={true}
      onClose={onClose}
      position={position}
      top={top}
      resizable={true}
      width={width}
      defaultWidth={defaultWidth}
      minWidth={minWidth}
      maxWidth={maxWidth}
      handleChangeWidth={onChangeWidth}
      handleClickOutside={() => false}
    >
      <div className={styles.content}>
        {isLoading || !!!reference ? (
          <div className={styles.skeleton}>
            <span className={cn(styles.text, styles.top)} />
            <span className={cn(styles.text, styles.bottom)} />
          </div>
        ) : (
          <>
            {isShowSourceLink ? (
              <div className={styles.source}>
                <Link
                  onClick={() => {
                    trackAction("clickReferenceSourceLink");
                  }}
                  // 直接の出典リンクがある場合はそちらを優先し、ない場合は PDF ビューアへのリンクを表示する
                  // 出典先が /pdf で始まる場合は PDF ビューアへのリンクとみなす
                  to={
                    reference.sourceLink &&
                    !reference.sourceLink.startsWith("/pdf")
                      ? reference.sourceLink
                      : reference.keyPdf
                      ? `/pdf-viewer?researchKeyPdf=${encodeURIComponent(
                          reference.keyPdf
                        )}`
                      : "#"
                  }
                  target="_blank"
                  className={styles.sourceLink}
                >
                  <span>出典ページを確認する</span>
                  <img src={ExternalImage} />
                </Link>
              </div>
            ) : (
              <p className={styles.noSource}>
                この情報の出典元ページは表示できません
              </p>
            )}
            <ReferenceContent reference={reference} />
          </>
        )}
      </div>
    </SideHelp>
  );
};
